import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type { Filterdescriptor } from "@/components/ui/data-table-filter";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import { useFilterStand } from "@/lib/useFilterStand";
import { reistijdCell, reistijdWaarde } from "./reistijdKolom";
import { useBeschikbaarheden, type Beschikbaarheid } from "./useDeelname";

const helper = createColumnHelper<typeof dataTableFeatures, Beschikbaarheid>();

const kolommen = (t: TFunction<"deelname">) =>
  helper.columns([
    helper.accessor("name", {
      header: t("ondersteuners.naam"),
      // Through to the persoon, where a beheerder may fill in beschikbaarheid namens them.
      cell: (info) => (
        <Link
          to="/ondersteuners"
          search={{ persoon: info.row.original.persoonId }}
          className="underline-offset-4 hover:underline"
        >
          {info.getValue()}
        </Link>
      ),
    }),
    helper.accessor((rij) => rij.functies.join(" "), {
      id: "functies",
      header: t("ondersteuners.aangeboden"),
      cell: (info) => {
        const rij = info.row.original;

        // Nothing offered is an answer, and it is the one a beheerder is counting.
        if (rij.functies.length === 0) {
          return (
            <span className="text-sm text-muted-foreground">
              {t("ondersteuners.nietsOpgegeven")}
            </span>
          );
        }

        return (
          <span className="flex flex-wrap gap-1">
            {rij.functies.map((functie) => (
              <Badge key={functie} variant="outline">
                {functie}
              </Badge>
            ))}
          </span>
        );
      },
    }),
    helper.accessor((rij) => reistijdWaarde(rij.reistijdSeconds), {
      id: "reistijd",
      header: t("gedeeld.reistijd"),
      cell: (info) => reistijdCell(info.getValue(), t),
    }),
  ]);

/** Who offered to staff this wedstrijddag, and who is bevoegd but said nothing. */
export function Beschikbaarheden({
  wedstrijddagId,
}: {
  wedstrijddagId: string;
}) {
  const { t } = useTranslation("deelname");
  const { data, isPending, error } = useBeschikbaarheden(wedstrijddagId);

  const descriptors = useMemo<Filterdescriptor<Beschikbaarheid>[]>(
    () => [
      {
        id: "opgegeven",
        label: t("ondersteuners.filterOpgegeven"),
        kind: "schakelaar",
        // On by default: the rest of the list is everybody who could have offered.
        standaard: { soort: "schakelaar", aan: true },
        past: (rij) => rij.functies.length > 0,
      },
      {
        id: "functie",
        label: t("ondersteuners.filterFunctie"),
        kind: "meerkeuze",
        opties: [
          ...new Map(
            (data ?? []).flatMap((rij) =>
              rij.functieIds.map(
                (id, index) => [id, rij.functies[index]] as const,
              ),
            ),
          ).entries(),
        ].map(([waarde, label]) => ({ waarde, label })),
        past: (rij, waarde) =>
          waarde.soort !== "meerkeuze" ||
          waarde.waarden.some((id) => rij.functieIds.includes(id)),
      },
    ],
    [data, t],
  );

  const { stand, onStand } = useFilterStand(descriptors, "/wedstrijddagen");

  if (error) {
    return <p className="text-sm text-destructive">{error.message}</p>;
  }

  if (isPending || !data) {
    return (
      <DataTableSkeleton
        label={t("ondersteuners.label")}
        columns={kolommen(t)}
        rows={6}
        search
      />
    );
  }

  return (
    <DataTable
      label={t("ondersteuners.label")}
      data={data}
      columns={kolommen(t)}
      getRowId={(rij) => rij.persoonId}
      searchPlaceholder={t("ondersteuners.zoek")}
      exportHref={exportHref(
        "/api/beheer/wedstrijddagen/{wedstrijddagId}/beschikbaarheden/export",
        { path: { wedstrijddagId } },
      )}
      clientPageSize={FULL_PAGE_SIZE}
      filters={{ descriptors, stand, onStand }}
    />
  );
}
